import axios from "axios";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { AuthContext } from "../../context/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import ChartData from "../../components/ChartData";
import { useTheme } from "../../context/ThemeContext";

const AnimatedNumber = ({ value, duration = 1500 }) => {
  const [count, setCount] = useState(0);
  const currentDisplayedValue = useRef(0);

  useEffect(() => {
    let startTime = null;
    const targetValue = parseInt(value, 10) || 0;
    const startValue = currentDisplayedValue.current;

    if (startValue === targetValue) {
      return;
    }

    let animationFrameId;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;

      const progress = currentTime - startTime;
      const percentage = Math.min(progress / duration, 1);
      const easeOut = percentage === 1 ? 1 : 1 - Math.pow(2, -10 * percentage);
      const nextCount = Math.floor(startValue + (targetValue - startValue) * easeOut);

      setCount(nextCount);
      currentDisplayedValue.current = nextCount;

      if (percentage < 1) {
        animationFrameId = requestAnimationFrame(animate);
      }
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationFrameId);
  }, [value, duration]);

  return <>{count}</>;
};

export default function IncidentsPerUsers({ selectedUser, incidents }) {
  const { apiUrl } = useContext(AuthContext);
  const { t } = useTranslation();
  const { theme } = useTheme();
  const currentMonthTag = new Date().toISOString().slice(0, 7);
  const [selectedMonth, setSelectedMonth] = useState(currentMonthTag);

  const isGlobal = !selectedUser || selectedUser === "";

  const fetchGlobalStats = async () => {
    const res = await axios.get(`${apiUrl}halo/get_stat`, {
      withCredentials: true,
    });
    return res.data;
  };

  const { data: globalData, isError: isErrorGlobal } = useQuery({
    queryKey: ["incidentsUserListGlobal"],
    queryFn: fetchGlobalStats,
  });

  const fetchUserStats = async () => {
    const res = await axios.get(`${apiUrl}halo/get_stat_user/${encodeURIComponent(selectedUser)}`, {
      withCredentials: true,
    });
    return res.data;
  };

  const { data: userData, isError: isErrorUser } = useQuery({
    queryKey: ["incidentsUserList", selectedUser],
    enabled: !isGlobal,
    queryFn: fetchUserStats,
  });

  const fetchHaloStatsVipUsers = async () => {
    const res = await axios.get(`${apiUrl}halo/get_stat_vip_users`, {
      withCredentials: true,
    });
    return res.data;
  };

  const { data: haloVipUsers } = useQuery({
    queryKey: ["haloVipUsers"],
    queryFn: fetchHaloStatsVipUsers,
  });

  const availableMonths = useMemo(() => {
    const monthSet = new Set([
      currentMonthTag,
      ...Object.keys(incidents?.graph_ticket_category_month || {}),
      ...Object.keys(haloVipUsers || {})
    ]);

    return Array.from(monthSet).sort((a, b) => b.localeCompare(a));
  }, [currentMonthTag, incidents?.graph_ticket_category_month, haloVipUsers]);

  const formatMonthLabel = (monthKey) => {
    const monthDate = new Date(`${monthKey}-01T00:00:00`);

    if (Number.isNaN(monthDate.getTime())) {
      return monthKey;
    }

    return new Intl.DateTimeFormat("fr-FR", {
      month: "long",
      year: "numeric"
    }).format(monthDate);
  };

  const categoryChartData = useMemo(() => {
    const monthData = incidents?.graph_ticket_category_month?.[selectedMonth] || [];

    return monthData.reduce((acc, item) => {
      const category = item.category || t("Non défini");
      const count = parseInt(item.ticket_count, 10) || 0;

      acc[category] = {
        [t("Nombre de tickets")]: count
      };

      return acc;
    }, {});
  }, [incidents?.graph_ticket_category_month, selectedMonth, t]);

  const ticketTypeColors = {
    Incident: "rgba(255, 149, 5, 0.8)",
    "Service Request": "#8977B0",
    "Change request": "#5bc69a",
    Question: "#E8EBED",
    Alerte: "#FF5D57",
    Autre: "#9f5927"
  };

  const vipUsersTicketTypes = useMemo(() => {
    const monthData = haloVipUsers?.[selectedMonth] || [];
    const filteredMonthData = isGlobal
      ? monthData
      : monthData.filter((item) => item.user === selectedUser);
    const ticketTypes = new Set();

    filteredMonthData.forEach((item) => {
      Object.keys(item.casetypecode || {}).forEach((ticketType) => {
        if (ticketType) {
          ticketTypes.add(ticketType);
        }
      });
    });

    return Array.from(ticketTypes);
  }, [haloVipUsers, isGlobal, selectedMonth, selectedUser]);

  const vipUsersChartData = useMemo(() => {
    const monthData = haloVipUsers?.[selectedMonth] || [];
    const filteredMonthData = isGlobal
      ? monthData
      : monthData.filter((item) => item.user === selectedUser);

    return filteredMonthData.reduce((acc, item) => {
      const userName = item.user || t("Non défini");
      const ticketTypesForUser = item.casetypecode || {};

      acc[userName] = vipUsersTicketTypes.reduce((ticketAcc, ticketType) => {
        ticketAcc[ticketType] = parseInt(ticketTypesForUser[ticketType], 10) || 0;
        return ticketAcc;
      }, {});

      return acc;
    }, {});
  }, [haloVipUsers, isGlobal, selectedMonth, selectedUser, t, vipUsersTicketTypes]);

  if (isErrorGlobal || isErrorUser) {
    return <div>{t("Erreur lors du chargement des statistiques.")}</div>;
  }

  const currentData = isGlobal ? (globalData || {}) : (userData || {});

  const rawTotalTickets = currentData.totalTickets || 0;
  const rawAvgTime = isGlobal ? (currentData.avgTimeGlobal || 0) : (currentData.avgTimeAllTime || 0);
  const rawMonthly = currentData.monthlyBreakdown || [];

  const rawAvgDirection = globalData?.avgTimeDirection || 0;
  const rawAvgCollaborateurs = globalData?.avgTimeCollaborateurs || 0;

  const displayData = {
    totalTickets: rawTotalTickets,
    avgTime: rawAvgTime,
    avgTimeDirection: rawAvgDirection,
    avgTimeCollaborateurs: rawAvgCollaborateurs,
    monthlyBreakdown: rawMonthly.map((item) => {
      const itemValue = isGlobal ? (item.value || 0) : (item.avgTimePerTicket || 0);
      return {
        month: item.month,
        value: itemValue
      };
    })
  };

  return (
    <div className="user-stats">
      <h1 className="user-stats-title">
        {isGlobal
          ? t("avg_time_per_user_title")
          : t("avg_time_specific_user", { name: currentData.name || currentData.user || selectedUser })}
      </h1>

      <div className="user-stats_row">
        <div className="user-stats_card main-kpi">
          <span className="user-stats_value">
            <AnimatedNumber value={displayData.totalTickets} /> tickets
          </span>
        </div>
        <div className="user-stats_card main-kpi">
          <span className="user-stats_value">
            <AnimatedNumber value={displayData.avgTime} /> minutes
          </span>
          <span className="user-stats_label">{t("avg_time_per_ticket")}</span>
        </div>
      </div>

      <div className="user-stats_row">
        <div className="user-stats_card full-width">
          <h3 className="user-stats_card-title">{t("avg_time_per_ticket")}</h3>
          <div className="user-stats_monthly-grid">
            {displayData.monthlyBreakdown.map((item, index) => {
              if (!item.month) return null;

              const [monthName, year] = item.month.split(" ");
              const translatedMonth = monthName ? t(monthName.toLowerCase()) : "";

              return (
                <div key={index} className="user-stats_month-item">
                  <span className="month-name">{translatedMonth} {year}</span>
                  <span className="month-value">
                    <AnimatedNumber value={item.value} /> m
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="user-stats_row">
        <div className="user-stats_card entity-kpi">
          <span className="user-stats_value">
            <AnimatedNumber value={displayData.avgTimeDirection} /> minutes
          </span>
          <span className="user-stats_label">{t("avg_time_direction")}</span>
        </div>
        <div className="user-stats_card entity-kpi">
          <span className="user-stats_value">
            <AnimatedNumber value={displayData.avgTimeCollaborateurs} /> minutes
          </span>
          <span className="user-stats_label">{t("avg_time_collaborators")}</span>
        </div>
      </div>

      <div className="page-section_form">
        <form className="form">
          <div className="form-input_container">
            <label htmlFor="incidents-users-month">{t("Sélectionnez le mois")}</label>
            <select
              className="only only_incidents"
              id="incidents-users-month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              {availableMonths.map((monthKey) => (
                <option key={monthKey} value={monthKey}>
                  {formatMonthLabel(monthKey)}
                </option>
              ))}
            </select>
          </div>
        </form>
      </div>

      <div className="user-stats_row user-stats_row-graphs">
        <div className="user-stats_card-graph">
          {Object.keys(categoryChartData).length > 0 ? (
            <ChartData
              key={`${theme}-category-${selectedMonth}`}
              dataForChart={categoryChartData}
              allLabels={[t("Nombre de tickets")]}
              isStacked={false}
              titleChart={t("Nombre de tickets par catégorie")}
              typeOfChart="bar"
              setNumberMaxTicksLimitY={true}
              customColors={{
                [t("Nombre de tickets")]: theme === "dark" ? "rgba(153, 122, 219, 0.85)" : "rgba(18, 65, 136, 0.85)"
              }}
              theme={theme}
              maxHeight={320}
            />
          ) : (
            <div className="user-stats_empty-state">
              <h3 className="user-stats_card_graph-title">{t("Aucune donnée disponible pour ce mois")}</h3>
            </div>
          )}
        </div>

        <div className="user-stats_card-graph">
          {Object.keys(vipUsersChartData).length > 0 && vipUsersTicketTypes.length > 0 ? (
            <ChartData
              key={`${theme}-vip-${selectedMonth}`}
              dataForChart={vipUsersChartData}
              allLabels={vipUsersTicketTypes}
              isStacked={true}
              titleChart={isGlobal ? t("Nombre de tickets par utilisateur") : t("Nombre de tickets pour l'utilisateur sélectionné")}
              typeOfChart="bar"
              setNumberMaxTicksLimitY={true}
              customColors={ticketTypeColors}
              theme={theme}
              maxHeight={320}
            />
          ) : (
            <div className="user-stats_empty-state user-stats_card-graph">
              <h3 className="user-stats_card_graph-title">{t("Aucune donnée disponible pour ce mois")}</h3>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
